import React from "react";
import { Box, Typography, Stack, useTheme, useMediaQuery } from "@mui/material";
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import HomeIcon from '@mui/icons-material/Home';
import EventIcon from '@mui/icons-material/Event';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import CheckIcon from '@mui/icons-material/Check';
import { useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useQuote } from "../context/QuoteContext";
import { formatQuoteScheduleRangeDisplay } from "../utils/javaLocalTime";

const customQuoteSteps = [
    { label: "Vehicle", icon: <DirectionsCarIcon />, path: "/quote/vehicle" },
    { label: "Services", icon: <BuildIcon />, path: "/quote/services" },
    { label: "Contact", icon: <ContactMailIcon />, path: "/quote/contact" },
];

const approvedQuoteSteps = [
    { label: "Location", icon: <HomeIcon />, path: "/quote/location" },
    { label: "Schedule", icon: <EventIcon />, path: "/quote/schedule" },
    { label: "Book", icon: <CheckIcon />, path: "/quote/book" },
];

// Helper functions to get actual data for each step
const getVehicleInfo = (quoteData) => {
    if (!quoteData.vehicle) return "Vehicle";
    
    const { year, make, model, license, vin } = quoteData.vehicle;
    
    if (year && make && model) {
        return `${year} ${make} ${model}`;
    } else if (license) {
        return `License: ${license}`;
    } else if (vin) {
        return `VIN: ${vin.substring(0, 8)}...`;
    }
    
    return "Vehicle";
};

const getServicesInfo = (cartItems) => {
    if (!cartItems || cartItems.length === 0) return "Services";
    
    const isDiagnosticLine = (item) =>
      item.type === 'diagnostic' || item.isCarWontStartDiagnosticLine;
    const diagnosticCount = cartItems.filter(isDiagnosticLine).length;
    const serviceCount = cartItems.filter((item) => !isDiagnosticLine(item)).length;
    
    if (diagnosticCount > 0 && serviceCount > 0) {
        return `${diagnosticCount} diagnostic, ${serviceCount} services`;
    } else if (diagnosticCount > 0) {
        return `${diagnosticCount} diagnostic service`;
    } else if (serviceCount > 0) {
        return `${serviceCount} services selected`;
    }
    
    return "Services";
};

const getLocationInfo = (quoteData) => {
    if (!quoteData.location) return "Location";
    
    const { address, city, state } = quoteData.location;
    
    if (address && city && state) {
        return `${address}, ${city}, ${state}`;
    } else if (address) {
        return address;
    }
    
    return "Location";
};

const getScheduleInfo = (quoteData) => {
    if (!quoteData.schedule) return "Schedule";
    
    const { date, time, preferredTimeSlot, startTime, endTime } = quoteData.schedule;
    
    const rangeDisplay =
      startTime != null && String(startTime).trim() !== ""
        ? formatQuoteScheduleRangeDisplay(startTime, endTime || "")
        : time != null && String(time).trim() !== ""
          ? formatQuoteScheduleRangeDisplay(time, endTime || "")
          : preferredTimeSlot != null && String(preferredTimeSlot).trim() !== ""
            ? formatQuoteScheduleRangeDisplay(preferredTimeSlot, endTime || "")
            : "";
    
    if (date && rangeDisplay) {
        return `${date} · ${rangeDisplay}`;
    }
    if (date) {
        return date;
    }
    if (rangeDisplay) {
        return rangeDisplay;
    }
    if (preferredTimeSlot) {
        return String(preferredTimeSlot);
    }
    
    return "Schedule";
};

const getContactInfo = (quoteData) => {
    if (!quoteData.contact) return "Contact";
    
    const { firstName, lastName, email } = quoteData.contact;
    
    if (firstName && lastName) {
        return `${firstName} ${lastName}`;
    } else if (firstName) {
        return firstName;
    } else if (email) {
        return email;
    }
    
    return "Contact";
};

const QuoteStepperNav = ({ currentStep }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();
    const navigate = useNavigate();
    const { cartItems } = useCart();
    const { quoteData, canAccessStep } = useQuote();

    // Determine which steps to show
    let steps;
    
    // Check if we're in approved quote flow (has quote ID)
    const isApprovedQuoteFlow = quoteData.id;
    
    if (isApprovedQuoteFlow) {
        // Show only location, schedule, book steps
        steps = approvedQuoteSteps;
    } else {
        // Custom quote flow: show only vehicle, services, contact
        steps = customQuoteSteps;
    }

    // Calculate active step based on completed steps, not current URL
    let activeStep;
    if (location.pathname === "/quote") {
        activeStep = -1; // No step highlighted on initial quote page
    } else if (typeof currentStep === 'number' && !isApprovedQuoteFlow) {
        // Only use currentStep prop for normal quote flow, not approved quote flow
        activeStep = currentStep;
    } else {
        if (isApprovedQuoteFlow) {
            // For approved quote flow, show current step as active
            const currentStepIndex = steps.findIndex(step => location.pathname === step.path);
            if (currentStepIndex >= 0) {
                activeStep = currentStepIndex;
            } else {
                // Fallback: find last completed step
                const stepOrder = ['location', 'schedule'];
                let lastCompletedIndex = -1;
                
                for (let i = 0; i < stepOrder.length; i++) {
                    const stepName = stepOrder[i];
                    if (quoteData?.completedSteps?.[stepName]) {
                        const stepIndex = steps.findIndex(step => {
                            const stepNameMap = {
                                '/quote/location': 'location',
                                '/quote/schedule': 'schedule',
                                '/quote/book': 'schedule'
                            };
                            return stepNameMap[step.path] === stepName;
                        });
                        if (stepIndex >= 0) {
                            lastCompletedIndex = stepIndex;
                        }
                    }
                }
                activeStep = lastCompletedIndex;
            }
        } else {
            // For custom quote flow, find the last completed step
            const stepOrder = ['vehicle', 'services', 'contact'];
            let lastCompletedIndex = -1;
            
            for (let i = 0; i < stepOrder.length; i++) {
                const stepName = stepOrder[i];
                if (quoteData?.completedSteps?.[stepName]) {
                    // Find this step in the current steps array
                    const stepIndex = steps.findIndex(step => {
                        const stepNameMap = {
                            '/quote/vehicle': 'vehicle',
                            '/quote/services': 'services',
                            '/quote/contact': 'contact'
                        };
                        return stepNameMap[step.path] === stepName;
                    });
                    if (stepIndex >= 0) {
                        lastCompletedIndex = stepIndex;
                    }
                }
            }
            
            activeStep = lastCompletedIndex;
        }
    }


    return (
        <Box sx={{ width: '100%', m: 0, p: 0 }}>
        <Box sx={{
            display: 'flex',
            justifyContent: { xs: 'space-around', sm: 'center' },
            alignItems: 'center',
            columnGap: { xs: 1, sm: 0 },
            pt: { xs: 0.5, sm: 1 },
            pb: { xs: 0.5, sm: 1 },
            px: { xs: 0, sm: 0 },
            m: 0,
            borderBottom: { xs: 'none', sm: `1px solid ${theme.palette.divider || 'rgba(255, 255, 255, 0.12)'}` },
            overflowX: 'auto',
            width: '100%',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
        }}>
            {steps.map((step, idx) => {
                // Map step paths to step names for validation
                const stepNameMap = isApprovedQuoteFlow ? {
                    '/quote/location': 'location',
                    '/quote/schedule': 'schedule',
                    '/quote/book': 'book'
                } : {
                    '/quote/vehicle': 'vehicle',
                    '/quote/services': 'services',
                    '/quote/contact': 'contact'
                };
                
                const stepName = stepNameMap[step.path];
                const canAccess = stepName ? canAccessStep(stepName) : true;
                const isClickable = canAccess && (idx <= activeStep || activeStep === -1);
                
                
                // Get actual data for this step
                let stepInfo = "";
                switch (step.label) {
                    case "Vehicle":
                        stepInfo = getVehicleInfo(quoteData);
                        break;
                    case "Services":
                        stepInfo = getServicesInfo(cartItems);
                        break;
                    case "Location":
                        stepInfo = getLocationInfo(quoteData);
                        break;
                    case "Schedule":
                        stepInfo = getScheduleInfo(quoteData);
                        break;
                    case "Contact":
                        stepInfo = getContactInfo(quoteData);
                        break;
                    default:
                        stepInfo = step.label;
                }
                
                return (
                    <Box
                        key={step.label}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flex: { xs: 1, sm: 'none' },
                            mx: { xs: 0.5, sm: 2, md: 3 },
                            position: 'relative',
                            cursor: isClickable ? 'pointer' : 'default',
                            opacity: idx > activeStep ? 0.6 : 1,
                            pointerEvents: isClickable ? 'auto' : 'none',
                            transition: 'all 0.2s',
                            minWidth: { xs: 0, sm: 'auto' },
                            flexShrink: 0,
                            '&:hover': isClickable ? { opacity: 1 } : {},
                        }}
                        onClick={() => isClickable && navigate(step.path)}
                    >
                        <Stack 
                            direction="row" 
                            alignItems="center" 
                            spacing={{ xs: 0.5, sm: 1 }}
                            sx={{ flexDirection: isMobile ? 'column' : 'row' }}
                        >
                            <Box sx={{ 
                                color: idx <= activeStep 
                                    ? '#fff' 
                                    : (theme.palette.dark?.text || 'rgba(255, 255, 255, 0.6)'),
                                transition: 'color 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '& svg': {
                                    fontSize: { xs: 18, sm: 20 }
                                }
                            }}>
                                {step.icon}
                            </Box>
                            <Typography
                                variant={isMobile ? "caption" : "body1"}
                                sx={{ 
                                    color: idx <= activeStep 
                                        ? '#fff' 
                                        : (theme.palette.dark?.text || 'rgba(255, 255, 255, 0.6)'),
                                    fontWeight: idx <= activeStep ? 600 : 500,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {step.label}
                            </Typography>
                        </Stack>
                        
                        {activeStep === idx && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: { xs: '-15%', sm: '-25%' },
                                    right: { xs: '-15%', sm: '-25%' },
                                    bottom: -2,
                                    height: { xs: 2, sm: 3 },
                                    background: '#fff',
                                    borderRadius: 2,
                                    zIndex: 1,
                                }}
                            />
                        )}
                    </Box>
                );
            })}
        </Box>
        </Box>
    );
};

export default QuoteStepperNav; 